import {
  BLOCKED_ROUTES,
  CHIP_DAYS,
  DAY_MS,
  HINT_DAYS,
  LEVELS,
  MAX_POPUP_PAGES,
  NEW_USER_GRACE_DAYS,
  OAUTH_RETURN,
  PANEL_DAYS,
  POPUP_GAP_DAYS,
  POPUP_WINDOW_DAYS,
  SURFACES
} from './constants';
import type { Post, Receipt, RuleContext, Surface } from './types';

// ==============================|| WHAT'S NEW — RULES ||============================== //
// Pure functions only: every decision about what to show is made here and unit-tested.

/** When a post goes live, in epoch ms (NaN for a malformed launchAt). */
export const launchTime = (post: Post): number => Date.parse(post.launchAt);
// Launches on the same UTC day are one group: they always arrive together.
const launchDay = (post: Post): string => new Date(launchTime(post)).toISOString().slice(0, 10);
const ageDays = (post: Post, now: number): number => (now - launchTime(post)) / DAY_MS;

/** Whether this post was already seen in that place. */
export const hasReceipt = (receipts: Receipt[], postId: string, surface: Surface): boolean =>
  receipts.some((receipt) => receipt.postId === postId && receipt.surface === surface);

// A post launched before someone signed up was already there on their first day, so it's never news to them.
const newToThem = (post: Post, ctx: RuleContext): boolean => ctx.signedUpAt === null || launchTime(post) >= ctx.signedUpAt;

const matchesAudience = (post: Post, ctx: RuleContext): boolean => {
  const { roles, entitledOnly } = post.audience ?? {};
  if (roles?.length && !roles.some((role) => ctx.roles.includes(role))) return false;
  // Billing still loading counts as "not yet": the post waits instead of flashing at the wrong people.
  return !entitledOnly || ctx.entitled === true;
};

// Chips and hints: launches and features only, new to this person, within their days, not yet seen there.
const freshOn = (post: Post, ctx: RuleContext, surface: Surface, days: number): boolean =>
  post.level !== LEVELS.fix && newToThem(post, ctx) && ageDays(post, ctx.now) <= days && !hasReceipt(ctx.receipts, post.id, surface);

// Read in the panel or shown in the popup: either way the person has had this news.
const alreadyRead = (post: Post, ctx: RuleContext): boolean =>
  hasReceipt(ctx.receipts, post.id, SURFACES.panel) || hasReceipt(ctx.receipts, post.id, SURFACES.popup);

/** Live posts for this person, newest first: launched, flag on, audience matched, and still kept by the panel. */
export const visiblePosts = (posts: Post[], ctx: RuleContext): Post[] =>
  posts
    .filter((post) => launchTime(post) <= ctx.now && ageDays(post, ctx.now) <= PANEL_DAYS)
    .filter((post) => !post.configFlag || ctx.flags[post.configFlag])
    .filter((post) => matchesAudience(post, ctx))
    .sort((a, b) => launchTime(b) - launchTime(a));

/** Posts counted on the What's new badge: new to this person and not yet opened in the panel or the popup. */
export const unreadPosts = (posts: Post[], ctx: RuleContext): Post[] =>
  visiblePosts(posts, ctx).filter((post) => newToThem(post, ctx) && !alreadyRead(post, ctx));

/**
 * The launch pages to pop up now, newest first, or [] for none. The 3-day gap never splits a same-day group:
 * a launch that went live after its group was first shown skips the gap. A page the person just didn't reach
 * was already live then, so it waits like any other.
 */
export function pickPopup(posts: Post[], ctx: RuleContext): Post[] {
  if (ctx.signedUpAt === null || ctx.now - ctx.signedUpAt < NEW_USER_GRACE_DAYS * DAY_MS) return [];

  const waiting = (c: RuleContext): Post[] =>
    visiblePosts(posts, c).filter(
      (post) => post.level === LEVELS.launch && newToThem(post, c) && ageDays(post, c.now) <= POPUP_WINDOW_DAYS && !alreadyRead(post, c)
    );
  // Billing still loading: a paid-only launch can't be placed yet, and showing the rest would split its day.
  if (ctx.entitled === null && waiting({ ...ctx, entitled: true }).some((post) => post.audience?.entitledOnly)) return [];

  const launches = visiblePosts(posts, ctx).filter((post) => post.level === LEVELS.launch);
  const popupReceipts = ctx.receipts.filter((receipt) => receipt.surface === SURFACES.popup);
  const lastPopupAt = popupReceipts.reduce((latest, receipt) => Math.max(latest, receipt.seenAt), -Infinity);
  const gapOver = ctx.now - lastPopupAt >= POPUP_GAP_DAYS * DAY_MS;

  const groupFirstShownAt = new Map<string, number>();
  launches.forEach((post) => {
    const receipt = popupReceipts.find((r) => r.postId === post.id);
    if (!receipt) return;
    const day = launchDay(post);
    groupFirstShownAt.set(day, Math.min(groupFirstShownAt.get(day) ?? Infinity, receipt.seenAt));
  });
  const arrivedLate = (post: Post): boolean => launchTime(post) > (groupFirstShownAt.get(launchDay(post)) ?? Infinity);

  return waiting(ctx)
    .filter((post) => gapOver || arrivedLate(post))
    .slice(0, MAX_POPUP_PAGES);
}

/** Posts whose menu item shows a NEW chip: for 30 days, or until the person opens that page. */
export const chipPosts = (posts: Post[], ctx: RuleContext): Post[] =>
  visiblePosts(posts, ctx).filter((post) => post.menuItemId && freshOn(post, ctx, SURFACES.chip, CHIP_DAYS));

/** Menu item ids that get a NEW chip. */
export const chipMenuIds = (posts: Post[], ctx: RuleContext): Set<string> =>
  new Set(chipPosts(posts, ctx).map((post) => post.menuItemId as string));

/** Posts whose one-time page hint may still show, newest first. The popup already told them, so it counts as the hint. */
export const hintPosts = (posts: Post[], ctx: RuleContext): Post[] =>
  visiblePosts(posts, ctx).filter(
    (post) => post.hintTarget && freshOn(post, ctx, SURFACES.hint, HINT_DAYS) && !hasReceipt(ctx.receipts, post.id, SURFACES.popup)
  );

/** True on that page or any page under it, matched per path segment. */
export const onPath = (pathname: string, path: string): boolean => pathname === path || pathname.startsWith(`${path}/`);

/** Pages where nothing may pop up; the popup waits for the next page. */
export const isBlockedRoute = (pathname: string, search: string): boolean =>
  BLOCKED_ROUTES.some((path) => onPath(pathname, path)) ||
  (onPath(pathname, OAUTH_RETURN.path) && new URLSearchParams(search).has(OAUTH_RETURN.param));
